import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Play, Clock, Video, Loader2, ExternalLink, Calendar, Crown, Lock, Youtube } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Free Classes Page
export const FreeClassesPage = () => {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const { getAuthHeader } = useAuth();

  useEffect(() => {
    fetchClasses();
  }, []);

  const fetchClasses = async () => {
    try {
      const response = await axios.get(`${API_URL}/classes?is_premium=false`, {
        headers: getAuthHeader()
      });
      setClasses(response.data);
    } catch (error) {
      console.error('Failed to fetch classes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePlay = (classItem) => {
    if (classItem.source === 'youtube' && classItem.video_id) {
      setSelectedVideo(classItem);
    } else if (classItem.video_url) {
      window.open(classItem.video_url, '_blank');
      toast.success(`Starting: ${classItem.title}`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="free-classes-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-stone-900 mb-2">Gentle Movement Classes</h1>
          <p className="text-stone-600">Chair yoga, Tai Chi, and Qigong to get you moving gently</p>
        </div>
        {classes.some(c => c.source === 'youtube') && (
          <div className="flex items-center gap-2 text-sm text-stone-500 bg-stone-100 px-3 py-1.5 rounded-full">
            <Youtube className="w-4 h-4 text-red-500" />
            Auto-synced from YouTube
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {classes.map((classItem) => (
          <div 
            key={classItem.id} 
            className="card-cosmic overflow-hidden card-hover group cursor-pointer"
            onClick={() => handlePlay(classItem)}
            data-testid={`class-card-${classItem.id}`}
          >
            <div className="aspect-video relative overflow-hidden">
              <img 
                src={classItem.thumbnail_url}
                alt={classItem.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-xl">
                  <Play className="w-8 h-8 text-primary ml-1" />
                </div>
              </div>

              <div className="absolute bottom-3 right-3 bg-black/60 backdrop-blur text-white px-2 py-1 rounded-lg flex items-center gap-1 text-sm">
                <Clock className="w-3 h-3" />
                {classItem.duration_minutes} min
              </div>

              <div className="absolute top-3 left-3 bg-secondary/90 text-secondary-foreground px-3 py-1 rounded-full text-xs font-semibold">
                FREE
              </div>

              {classItem.source === 'youtube' && (
                <div className="absolute top-3 right-3">
                  <Youtube className="w-6 h-6 text-red-500 drop-shadow-lg" />
                </div>
              )}
            </div>
            
            <div className="p-5">
              <h3 className="text-lg font-bold text-stone-900 mb-2 line-clamp-2">{classItem.title}</h3>
              <p className="text-sm text-stone-600 line-clamp-2">{classItem.description}</p>
            </div>
          </div>
        ))}
      </div>

      {classes.length === 0 && (
        <div className="card-cosmic p-12 text-center">
          <Video className="w-12 h-12 text-stone-400 mx-auto mb-4" />
          <p className="text-stone-600">No free classes available yet. Check back soon!</p>
        </div>
      )}

      {/* Video Player Modal */}
      <Dialog open={!!selectedVideo} onOpenChange={() => setSelectedVideo(null)}>
        <DialogContent className="sm:max-w-4xl p-0 overflow-hidden">
          <DialogHeader className="p-4 pb-0">
            <DialogTitle className="font-syne pr-8">{selectedVideo?.title}</DialogTitle>
          </DialogHeader>
          <div className="aspect-video w-full">
            {selectedVideo && (
              <iframe
                width="100%"
                height="100%"
                src={`https://www.youtube.com/embed/${selectedVideo.video_id}?autoplay=1&rel=0`}
                title={selectedVideo.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Premium Classes Page
export const PremiumClassesPage = () => {
  const [classes, setClasses] = useState([]);
  const [liveClasses, setLiveClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const { getAuthHeader, isPremium } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isPremium) {
      navigate('/pricing');
      return;
    }
    fetchClasses();
  }, [isPremium]);

  const fetchClasses = async () => {
    try {
      const response = await axios.get(`${API_URL}/classes?is_premium=true`, {
        headers: getAuthHeader()
      });
      const allClasses = response.data;
      setLiveClasses(allClasses.filter(c => c.is_live));
      setClasses(allClasses.filter(c => !c.is_live));
    } catch (error) {
      console.error('Failed to fetch classes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePlay = (classItem) => {
    if (classItem.source === 'youtube' && classItem.video_id) {
      setSelectedVideo(classItem);
    } else if (classItem.video_url) {
      window.open(classItem.video_url, '_blank');
    } else if (classItem.thrivecart_url) {
      window.open(classItem.thrivecart_url, '_blank');
    }
    toast.success(`Opening: ${classItem.title}`);
  };

  const handleJoinLive = (classItem) => {
    if (classItem.zoom_link) {
      window.open(classItem.zoom_link, '_blank');
      toast.success('Joining Zoom class...');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="premium-classes-page">
      <div>
        <div className="flex items-center gap-3 mb-2">
          <h1 className="text-3xl font-bold text-stone-900">Premium Classes</h1>
          <span className="badge-premium flex items-center gap-1">
            <Crown className="w-3 h-3" /> Premium
          </span>
        </div>
        <p className="text-stone-600">Full-length chair yoga, Tai Chi, Qigong, and live Zoom sessions</p>
      </div>

      {/* Live Classes Section */}
      {liveClasses.length > 0 && (
        <div>
          <h2 className="text-xl font-bold text-stone-900 mb-4 flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
            Live Classes
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {liveClasses.map((classItem) => (
              <div 
                key={classItem.id} 
                className="card-cosmic overflow-hidden border-2 border-accent/30"
                data-testid={`live-class-card-${classItem.id}`}
              >
                <div className="aspect-video relative overflow-hidden">
                  <img 
                    src={classItem.thumbnail_url}
                    alt={classItem.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  
                  <div className="absolute top-3 left-3 bg-red-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
                    LIVE
                  </div>

                  <div className="absolute bottom-3 right-3 bg-black/60 backdrop-blur text-white px-2 py-1 rounded-lg flex items-center gap-1 text-sm">
                    <Clock className="w-3 h-3" />
                    {classItem.duration_minutes} min
                  </div>
                </div>
                
                <div className="p-5">
                  <h3 className="text-lg font-bold text-stone-900 mb-2">{classItem.title}</h3>
                  <p className="text-sm text-stone-600 mb-4">{classItem.description}</p>
                  
                  {classItem.live_date && (
                    <div className="flex items-center gap-2 text-sm text-stone-500 mb-4">
                      <Calendar className="w-4 h-4" />
                      {classItem.live_date}
                    </div>
                  )}

                  <Button
                    onClick={() => handleJoinLive(classItem)}
                    className="w-full btn-cosmic"
                    data-testid={`join-live-btn-${classItem.id}`}
                  >
                    <Video className="w-4 h-4 mr-2" />
                    Join on Zoom
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recorded Classes Section */}
      <div>
        <h2 className="text-xl font-bold text-stone-900 mb-4">Recorded Classes</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {classes.map((classItem) => (
            <div 
              key={classItem.id} 
              className="card-cosmic overflow-hidden card-hover group cursor-pointer"
              onClick={() => handlePlay(classItem)}
              data-testid={`recorded-class-card-${classItem.id}`}
            >
              <div className="aspect-video relative overflow-hidden">
                <img 
                  src={classItem.thumbnail_url}
                  alt={classItem.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-xl">
                    <Play className="w-8 h-8 text-primary ml-1" />
                  </div>
                </div>

                <div className="absolute bottom-3 right-3 bg-black/60 backdrop-blur text-white px-2 py-1 rounded-lg flex items-center gap-1 text-sm">
                  <Clock className="w-3 h-3" />
                  {classItem.duration_minutes} min
                </div>

                <div className="absolute top-3 left-3 badge-premium text-xs">
                  PREMIUM
                </div>

                {classItem.source === 'youtube' && (
                  <div className="absolute top-3 right-3">
                    <Youtube className="w-6 h-6 text-red-500 drop-shadow-lg" />
                  </div>
                )}
              </div>
              
              <div className="p-5">
                <h3 className="text-lg font-bold text-stone-900 mb-2 line-clamp-2">{classItem.title}</h3>
                <p className="text-sm text-stone-600 mb-4 line-clamp-2">{classItem.description}</p>
                
                {classItem.thrivecart_url && (
                  <Button
                    onClick={(e) => { e.stopPropagation(); window.open(classItem.thrivecart_url, '_blank'); }}
                    variant="outline"
                    className="w-full flex items-center justify-center gap-2"
                    data-testid={`thrivecart-btn-${classItem.id}`}
                  >
                    Watch on ThriveCart
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {classes.length === 0 && liveClasses.length === 0 && (
        <div className="card-cosmic p-12 text-center">
          <Video className="w-12 h-12 text-stone-400 mx-auto mb-4" />
          <p className="text-stone-600">No premium classes available yet. Check back soon!</p>
        </div>
      )}

      {/* Video Player Modal */}
      <Dialog open={!!selectedVideo} onOpenChange={() => setSelectedVideo(null)}>
        <DialogContent className="sm:max-w-4xl p-0 overflow-hidden">
          <DialogHeader className="p-4 pb-0">
            <DialogTitle className="font-syne pr-8">{selectedVideo?.title}</DialogTitle>
          </DialogHeader>
          <div className="aspect-video w-full">
            {selectedVideo && (
              <iframe
                width="100%"
                height="100%"
                src={`https://www.youtube.com/embed/${selectedVideo.video_id}?autoplay=1&rel=0`}
                title={selectedVideo.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// Session Replays Page (Founding Members + VIP)
export const SessionReplaysPage = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const { getAuthHeader, isFoundingMember, isVip } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Check access
    if (!isFoundingMember && !isVip) {
      navigate('/dashboard/pricing');
      return;
    }
    fetchReplays();
  }, [isFoundingMember, isVip]);

  const fetchReplays = async () => {
    try {
      const response = await axios.get(`${API_URL}/session-replays`, {
        headers: getAuthHeader()
      });
      setVideos(response.data);
    } catch (error) {
      console.error('Failed to fetch session replays:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePlay = (video) => {
    if (video.source === 'youtube' && video.video_id) {
      setSelectedVideo(video);
    } else if (video.video_url) {
      window.open(video.video_url, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="session-replays-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-3xl font-bold text-stone-900">Session Replays</h1>
            <span className="px-3 py-1 text-xs font-semibold rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white">
              Founding Member
            </span>
          </div>
          <p className="text-stone-600">Watch recordings of past live sessions and workshops</p>
        </div>
        {videos.some(v => v.source === 'youtube') && (
          <div className="flex items-center gap-2 text-sm text-stone-500 bg-stone-100 px-3 py-1.5 rounded-full">
            <Youtube className="w-4 h-4 text-red-500" />
            Auto-synced from YouTube
          </div>
        )}
      </div>

      {videos.length === 0 ? (
        <div className="card-cosmic p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-purple-100 flex items-center justify-center">
            <Play className="w-8 h-8 text-purple-500" />
          </div>
          <h2 className="text-xl font-bold text-stone-900 mb-2">Session Replays Coming Soon</h2>
          <p className="text-stone-600 max-w-md mx-auto">
            Recordings of live sessions and workshops will appear here. Check back after our next live event!
          </p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {videos.map((video) => (
            <div 
              key={video.id} 
              className="card-cosmic overflow-hidden card-hover group cursor-pointer"
              onClick={() => handlePlay(video)}
              data-testid={`replay-card-${video.id}`}
            >
              <div className="aspect-video relative overflow-hidden">
                <img 
                  src={video.thumbnail_url || 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=400'}
                  alt={video.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-xl">
                    <Play className="w-8 h-8 text-purple-500 ml-1" />
                  </div>
                </div>

                {video.duration_minutes && (
                  <div className="absolute bottom-3 right-3 bg-black/60 backdrop-blur text-white px-2 py-1 rounded-lg flex items-center gap-1 text-sm">
                    <Clock className="w-3 h-3" />
                    {video.duration_minutes} min
                  </div>
                )}

                <div className="absolute top-3 left-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                  REPLAY
                </div>

                {video.source === 'youtube' && (
                  <div className="absolute top-3 right-3">
                    <Youtube className="w-5 h-5 text-white drop-shadow-lg" />
                  </div>
                )}
              </div>
              
              <div className="p-4">
                <h3 className="font-bold text-stone-900 mb-1 line-clamp-2">{video.title}</h3>
                {video.published_at && (
                  <p className="text-sm text-stone-500 flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {new Date(video.published_at).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Video Player Dialog */}
      <Dialog open={!!selectedVideo} onOpenChange={() => setSelectedVideo(null)}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden">
          <DialogHeader className="p-4 pb-0">
            <DialogTitle className="font-syne pr-8">{selectedVideo?.title}</DialogTitle>
          </DialogHeader>
          <div className="aspect-video w-full">
            {selectedVideo && (
              <iframe
                width="100%"
                height="100%"
                src={`https://www.youtube.com/embed/${selectedVideo.video_id}?autoplay=1&rel=0`}
                title={selectedVideo.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};